import { SalesContext } from "@database/contexts";
import { CreateSaleType, ErrorType, ReturnEntry } from "@utilities/types";
import { useCallback, useContext, useState } from "react";
import { useOrganisation } from "./useOrganisation";
import { salesService } from "@database/services";
import { useUser } from "./useUser";

export const useSales = () => {
    
    const {
        sales, setSales,
        selectedSale, setSelectedSale
    } = useContext(SalesContext);
    const { organisation } = useOrganisation();
    const { user } = useUser();

    const [createSaleError, setCreateSaleError] = useState<ErrorType | null>(null);
    const [createSaleLoading, setCreateSaleLoading] = useState<boolean>(false);
    const [getAllSalesLoading, setGetAllSalesLoading] = useState<boolean>(false);
    const [getAllSalesError, setGetAllSalesError] = useState<ErrorType | null>(null);
    const [returnProductLoading, setReturnProductLoading] = useState<boolean>(false);
    const [returnProductError, setReturnProductError] = useState<ErrorType | null>(null);
    const [returnProductSuccess, setReturnProductSuccess] = useState<boolean>(false);

    const onCreateSale = useCallback(async(saleData: Partial<CreateSaleType>)=> {
        if(!organisation || !user) return;

        setCreateSaleLoading(true);
        const req = await salesService.createSale({
            ...saleData,
            organisationID: organisation._id,
            sellerToken: user.token!,
        });

        if(req.code === 201){
            setSelectedSale(req.data.sale);
        } else {
            setCreateSaleError({
                message: (req.code === 400 || req.code === 404) ? 
                        "Données invalides ou stocks insuffisants" : "Une erreur est survenue",
            });
        }
        setCreateSaleLoading(false);
    }, [organisation, user, setSelectedSale]);

    const onGetAllSales = useCallback(async() => {
        if(!organisation) return;

        setGetAllSalesLoading(true);
        const req = await salesService.getAllSales(organisation._id);

        if(req.code === 200){
            setSales(req.data.data);
        } else {
            setGetAllSalesError({
                message: (req.code === 400 || req.code === 404) ? 
                        "Données invalides, erreur de création" : "Une erreur est survenue",
            });
        }
        setGetAllSalesLoading(false);
    }, [organisation, setSales]);

    const onReturnProduct = useCallback(async(saleID: string, returnEntry: ReturnEntry) => {
        if(!organisation) return;

        setReturnProductLoading(true);
        const req = await salesService.returnProduct(saleID, returnEntry);

        if(req.code === 200){
            setReturnProductSuccess(true);
            await onGetAllSales();
        } else {
            setReturnProductError({
                message: (req.code === 400 || req.code === 404) ? 
                        "Données invalides, erreur de création" : "Une erreur est survenue",
            });
        }
        setReturnProductLoading(false);
    }, [onGetAllSales, organisation]);

    return {
        sales, selectedSale, setSelectedSale,
        createSaleError, createSaleLoading,
        getAllSalesLoading, getAllSalesError, returnProductLoading, 
        returnProductError, returnProductSuccess,
        onCreateSale, onGetAllSales, onReturnProduct,
    };
};
